<template>
	<glass-card
		class="container p-5 my-5 signin"
		borderColor="#4ade80aa"
		background="#0b1220aa"
		blur="12px">
		<h1>Welcome back</h1>
		<p class="mt-2">
			Log meals smarter. Track macros, get insights, feel better.
		</p>
		<div class="mt-2">
			<small>
				New here?
				<router-link to="/signup">Create one</router-link>
			</small>
		</div>
		<o-auth />
		<hr />
		<form class="m-auto pt-3" @submit.prevent="submit" style="max-width: 500px">
			<div class="form-floating mb-3">
				<input
					type="email"
					class="form-control"
					placeholder="Email"
					v-model.trim="email" />
				<label class="form-label" for="email">Email</label>
			</div>
			<password-input v-model="pass" label="Password" :transparent="true" />
			<div class="ms-auto text-end">
				<router-link to="/forgot-password">Forgot password?</router-link>
			</div>
			<turnstile
				siteKey="0x4AAAAAAA0HUE46ATCN9t1U"
				class="my-3 d-flex justify-content-center"
				@token="token" />
			<div class="d-flex justify-content-between align-items-center mb-3">
				<div
					class="form-check"
					v-if="Capacitor.isNativePlatform() && biometricsAvailable">
					<input
						type="checkbox"
						class="form-check-input"
						id="registerBiometrics"
						v-model="registerBiometrics" />
					<label class="form-check-label" for="registerBiometrics">
						Register creds with biometrics for future logins on this device
					</label>
				</div>
			</div>

			<div class="text-center">
				<button class="btn bprimary btn-lg" type="submit" :disabled="!tToken">
					Sign in
				</button>
			</div>
		</form>

		<!-- login with biometrics instead -->
		<div
			class="text-center mt-3 mb-5"
			v-if="Capacitor.isNativePlatform() && biometricsAvailable">
			<hr />
			<h5>OR</h5>
			<button
				class="btn btn-primary"
				@click="performBiometricVerification"
				:disabled="!tToken">
				<i class="bi bi-fingerprint"></i> Login with Biometrics
			</button>
		</div>

		<!-- Create account and forgot password links repositioned above -->

		<div class="mt-5 fs-6">
			By continuing, you agree to the Terms and Privacy Policy.
		</div>
	</glass-card>
</template>
<script setup>
	import { ref, onMounted } from "vue"
	import Swal from "sweetalert2"
	import Turnstile from "@/components/Turnstile.vue"
	import { startLoading, stopLoading } from "@/utils/loaderBus"
	import { useTokenStore } from "@/stores/token"
	import { useRouter } from "vue-router"
	import { NativeBiometric, BiometryType } from "@/plugins/biometrics"
	import { Capacitor } from "@capacitor/core"
	import PasswordInput from "@/components/PasswordInput.vue"
	import OAuth from "@/components/OAuth.vue"
	import GlassCard from "@/components/GlassCard.vue"

	const pass = ref("")
	const email = ref("")
	const registerBiometrics = ref(false)
	const biometricsAvailable = ref(false)

	let tToken = ref("")

	const tokenStore = useTokenStore()
	const router = useRouter()

	onMounted(async () => {
		if (Capacitor.isNativePlatform()) {
			const result = await NativeBiometric.isAvailable()
			biometricsAvailable.value = result.isAvailable
		}
	})

	async function performBiometricVerification() {
		// const result = await NativeBiometric.isAvailable()

		if (!biometricsAvailable.value) {
			return
		}

		const verified = await NativeBiometric.verifyIdentity({
			reason: "For easy log in",
			title: "Log in",
			// subtitle: "Maybe add subtitle here?",
			// description: "Maybe a description too?",
		})
			.then(() => true)
			.catch(() => false)

		if (!verified) {
			Swal.fire({ icon: "error", title: "Could not verify identity" })
			return
		}

		try {
			const credentials = await NativeBiometric.getCredentials({
				server: "grubvision.com",
			})

			if (credentials?.username && credentials?.password) {
				email.value = credentials.username
				pass.value = credentials.password
				submit()
			}
		} catch (e) {
			Swal.fire({ icon: "error", title: "No biometric credentials found" })
		}
	}

	async function submit() {
		if (!pass.value) {
			Swal.fire({ icon: "error", title: "Please fill out the input box" })
			return
		}
		startLoading()
		try {
			const resp = await fetch("/api/signin", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					email: email.value,
					password: pass.value,
					token: tToken.value,
				}),
			})
			const data = await resp.json()
			if (data.message || data.error) {
				Swal.fire({ icon: "error", title: data.message ?? data.error })
				window.turnstile.reset()
				return
			}

			await tokenStore.setTokens(data.access_token, data.refresh_token)

			// Save biometric credentials if requested
			if (registerBiometrics.value && Capacitor.isNativePlatform()) {
				// const result = await NativeBiometric.isAvailable()
				// if (result.isAvailable) {
				// if (biometricsAvailable.value) {
				try {
					await NativeBiometric.setCredentials({
						username: email.value,
						password: pass.value,
						server: "grubvision.com",
					})
				} catch (e) {
					console.error("Failed to save biometric credentials:", e)
				}
				// }
			}

			router.push({ name: "dashboard" })
		} catch (e) {
			console.log(e)
			Swal.fire({ icon: "error", title: e.message })
			window.turnstile.reset()
		} finally {
			stopLoading()
		}
	}

	function token(t) {
		tToken.value = t
	}

	// function parseJwt(token) {
	// 	try {
	// 		return JSON.parse(atob(token.split(".")[1]))
	// 	} catch (e) {
	// 		return null
	// 	}
	// }
</script>

<style scoped>
	h1 {
		color: #ff6400 !important;
	}

	.signin {
		max-width: 500px;
		margin: auto;
	}

	.form-check {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		justify-content: flex-start;
	}

	.form-check-input {
		margin-top: 0;
		margin-left: 0;
	}

	/* Transparent inputs for this view */
	.form-control {
		background-color: transparent !important;
		color: #ffffff !important;
		border-color: rgba(255, 255, 255, 0.25);
	}
	.form-control:focus {
		background-color: transparent !important;
		color: #ffffff !important;
		box-shadow: 0 0 0 0.2rem rgba(34, 197, 94, 0.25);
		border-color: #22c55e;
	}
	.form-floating > label {
		background-color: transparent !important;
		color: #cbd5e1;
	}
	.form-control::placeholder {
		color: #94a3b8;
		opacity: 0.8;
	}

	/* Button style */

	.bprimary {
		width: 100%;
		border: none;
		border-radius: 14px;
		padding: 12px 16px;
		font-weight: 700;
		font-size: 1.05rem;
		background: linear-gradient(180deg, #34d399, #22c55e);
		color: #062312;
		cursor: pointer;
		box-shadow: 0 10px 24px #22c55e55, inset 0 1px 0 #fff8;
		transition: transform 0.06s ease, box-shadow 0.2s ease, filter 0.2s ease;
	}
	.bprimary:hover {
		transform: translateY(-1px);
		filter: brightness(1.02);
	}
	.bprimary:active {
		transform: translateY(0);
		box-shadow: 0 6px 16px #22c55e55;
	}
</style>
